import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import apiService from '../services/apiService';

const TAX_RATE = 0.05;
const PLATFORM_FEE = 5;

const PAYMENT_METHODS = [
  { id: 'online_payment', label: 'Online Payment', sub: 'Pay securely via Stripe', icon: '💳' },
  { id: 'pay_on_delivery', label: 'Pay on Pickup', sub: 'Cash or card when you pick up', icon: '💵' },
];

const TIP_PERCENTAGES = [
  { label: 'None', pct: 0 },
  { label: '10%', pct: 10 },
  { label: '15%', pct: 15 },
  { label: '20%', pct: 20 },
  { label: 'Custom', pct: -1 },
];

function formatTime(t) {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, refreshProfile } = useAuth();
  const { clearCart } = useCart();

  const params = location.state || {};
  const {
    cartItems = [],
    cartRestaurant = null,
    subtotal = 0,
    discount = 0,
    appliedCoupon = null,
    selectedDate,
    selectedTime,
    selectedTimeDisplay,
    cookingInstructions = '',
    kitchenId,
    tip = 0,
  } = params;

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [selectedTipPct, setSelectedTipPct] = useState(0); // 0 = None, -1 = Custom
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');

  const selectedTip = selectedTipPct === -1
    ? (parseFloat(customTipAmount) || 0)
    : parseFloat(((selectedTipPct / 100) * subtotal).toFixed(2));

  // Re-validate auth token on mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer');
      return;
    }
    if (cartItems.length === 0) {
      navigate('/customer');
      return;
    }
    // Refresh profile to validate token
    refreshProfile().catch(() => {});
  }, []);

  const tax = subtotal * TAX_RATE;
  const total = Math.max(0, subtotal + PLATFORM_FEE + tax + selectedTip - discount);

  const pickupDateLabel = selectedDate
    ? new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    : '—';

  const buildOrderData = (pmLabel) => {
    const [ph, pm] = (selectedTime || '12:00').split(':').map(Number);
    const pickupStart = new Date(selectedDate);
    pickupStart.setHours(ph, pm, 0, 0);
    const pickupEnd = new Date(pickupStart);
    pickupEnd.setMinutes(pickupEnd.getMinutes() + 30);

    return {
      kitchen_id: kitchenId,
      items: cartItems.map(item => ({
        menu_item_id: item.id,
        quantity: item.quantity,
        unit_price: item.price,
        cooking_request: item.cookingRequest || '',
      })),
      pickup_date: selectedDate,
      pickup_time: selectedTime,
      pickup_start: pickupStart.toISOString(),
      pickup_end: pickupEnd.toISOString(),
      slot_start_time: pickupStart.toISOString(),
      slot_end_time: pickupEnd.toISOString(),
      subtotal,
      tax_amount: tax,
      platform_fee: PLATFORM_FEE,
      tip_amount: selectedTip,
      discount_amount: discount,
      total_amount: total,
      payment_method: pmLabel,
      coupon_code: appliedCoupon?.code || null,
      cooking_instructions: cookingInstructions,
      order_type: 'pickup',
      delivery_type: 'pickup',
    };
  };

  const handlePlaceOrder = async () => {
    if (!paymentMethod) { setError('Please select a payment method'); return; }
    setError('');
    setPlacing(true);

    try {
      if (paymentMethod === 'online_payment') {
        const orderData = buildOrderData('stripe');
        const res = await apiService.createStripeCheckoutSession({
          ...orderData,
          success_url: `${window.location.origin}/customer/order-confirmation?success=1`,
          cancel_url: `${window.location.origin}/customer/checkout`,
        });

        if (res?.success && res.data?.checkout_url) {
          clearCart();
          window.location.href = res.data.checkout_url;
        } else {
          const orderRes = await apiService.createOrder(buildOrderData('pay_on_delivery'));
          if (orderRes?.success) {
            clearCart();
            navigate('/customer/order-confirmation', {
              state: { orderId: orderRes.data?.order?.id || orderRes.data?.id, total },
            });
          } else {
            const errMsg = orderRes?.error || 'Failed to place order';
            if (errMsg.toLowerCase().includes('customer not found') || errMsg.toLowerCase().includes('not found')) {
              setError('Your session has expired. Please log out and log back in to place an order.');
            } else {
              setError(errMsg);
            }
          }
        }
      } else {
        const orderData = buildOrderData('pay_on_delivery');
        const res = await apiService.createOrder(orderData);
        if (res?.success) {
          clearCart();
          navigate('/customer/order-confirmation', {
            state: { orderId: res.data?.order?.id || res.data?.id, total },
          });
        } else {
          const errMsg = res?.error || 'Failed to place order';
          if (errMsg.toLowerCase().includes('customer not found') || errMsg.toLowerCase().includes('not found')) {
            setError('Your session has expired. Please log out and log back in, then try again.');
          } else {
            setError(errMsg);
          }
        }
      }
    } catch (e) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="checkout-page">
      {/* Header */}
      <div className="checkout-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h2 className="checkout-title">Order Summary</h2>
      </div>

      {/* Progress Steps - matching mobile app */}
      <div className="checkout-progress">
        {['Restaurant', 'Items', 'Pickup', 'Review', 'Payment'].map((step, i) => {
          const isActive = i === 3;
          const isDone = i < 3;
          return (
            <React.Fragment key={step}>
              <div className={`progress-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
                <div className="progress-dot">
                  {isDone ? '✓' : i + 1}
                </div>
                <span className="progress-label">{step}</span>
              </div>
              {i < 4 && <div className={`progress-connector ${isDone ? 'done' : ''}`} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="checkout-content">
        {/* Restaurant */}
        <div className="checkout-section">
          <p className="checkout-section-label">RESTAURANT</p>
          <div className="checkout-restaurant-row">
            <div className="checkout-restaurant-icon">🍛</div>
            <div className="checkout-restaurant-info">
              <h4>{cartRestaurant?.name || 'Restaurant'}</h4>
              <p>Pickup order · Cash on Delivery</p>
            </div>
          </div>
        </div>

        {/* Pickup Details */}
        <div className="checkout-section">
          <p className="checkout-section-label">PICKUP DETAILS</p>
          <div className="pickup-detail-icon-row">
            <span className="pickup-detail-icon">🏪</span>
            <div>
              <span className="pickup-detail-label">ORDER TYPE</span>
              <p className="pickup-detail-value">Pickup</p>
            </div>
          </div>
          <div className="pickup-detail-icon-row">
            <span className="pickup-detail-icon">📅</span>
            <div>
              <span className="pickup-detail-label">PICKUP DATE</span>
              <p className="pickup-detail-value">{pickupDateLabel}</p>
            </div>
          </div>
          <div className="pickup-detail-icon-row">
            <span className="pickup-detail-icon">🕐</span>
            <div>
              <span className="pickup-detail-label">PICKUP TIME</span>
              <p className="pickup-detail-value">{selectedTimeDisplay || formatTime(selectedTime)}</p>
            </div>
          </div>
          <button className="edit-pickup-btn" onClick={() => navigate(-1)}>
            ✏️ Edit Pickup Details
          </button>
        </div>

        {/* Items */}
        <div className="checkout-section">
          <div className="checkout-section-header-row">
            <p className="checkout-section-label">YOUR ORDER</p>
            <button className="edit-cart-btn" onClick={() => navigate(-1)}>Edit Cart</button>
          </div>
          {cartItems.map(item => (
            <div key={item.id} className="checkout-item-row">
              {item.is_veg !== undefined && (
                <span className={`veg-dot-small ${item.is_veg ? 'veg' : 'non-veg'}`} />
              )}
              <span className="checkout-item-name">{item.name}</span>
              <span className="checkout-item-qty-right">×{item.quantity}</span>
              <span className="checkout-item-price">${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          {cookingInstructions && (
            <p className="checkout-instructions">Note: {cookingInstructions}</p>
          )}
        </div>

        {/* Tip - matching mobile */}
        <div className="checkout-section">
          <p className="checkout-section-label">ADD A TIP 💚</p>
          <p className="tip-subtitle">Support the kitchen with a tip</p>
          <div className="tip-options">
            {TIP_PERCENTAGES.map(opt => (
              <button
                key={opt.label}
                className={`tip-chip ${selectedTipPct === opt.pct ? 'selected' : ''}`}
                onClick={() => { setSelectedTipPct(opt.pct); if (opt.pct !== -1) setCustomTipAmount(''); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {selectedTipPct === -1 && (
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-input"
              placeholder="Enter custom tip ($)"
              value={customTipAmount}
              onChange={(e) => setCustomTipAmount(e.target.value)}
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        {/* Bill */}
        <div className="checkout-section">
          <p className="checkout-section-label">BILL DETAILS</p>
          <div className="bill-row"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          <div className="bill-row"><span>Platform Fee</span><span>${PLATFORM_FEE.toFixed(2)}</span></div>
          <div className="bill-row"><span>Tax (5%)</span><span>${tax.toFixed(2)}</span></div>
          {selectedTip > 0 && <div className="bill-row"><span>Tip</span><span>${selectedTip.toFixed(2)}</span></div>}
          {discount > 0 && (
            <div className="bill-row discount-row">
              <span>Discount {appliedCoupon ? `(${appliedCoupon.code})` : ''}</span>
              <span>-${discount.toFixed(2)}</span>
            </div>
          )}
          <hr className="price-divider" />
          <div className="bill-row total-row"><span>Total</span><span>${total.toFixed(2)}</span></div>
        </div>

        {/* Payment Method */}
        <div className="checkout-section">
          <p className="checkout-section-label">PAYMENT METHOD</p>
          {PAYMENT_METHODS.map(pm => (
            <button
              key={pm.id}
              className={`payment-method-card ${paymentMethod === pm.id ? 'selected' : ''}`}
              onClick={() => setPaymentMethod(pm.id)}
            >
              <span className="pm-icon">{pm.icon}</span>
              <div className="pm-info">
                <span className="pm-label">{pm.label}</span>
                <span className="pm-sub">{pm.sub}</span>
              </div>
              <span className="pm-check">{paymentMethod === pm.id ? '✓' : ''}</span>
            </button>
          ))}
        </div>

        {error && <div className="checkout-error">{error}</div>}
      </div>

      {/* Sticky bottom - total + place order */}
      <div className="checkout-sticky-bottom">
        <div className="checkout-total-bar">
          <div>
            <p className="checkout-total-label">Total</p>
            <p className="checkout-total-amount">${total.toFixed(2)}</p>
          </div>
          <button
            className="btn-place-order"
            onClick={handlePlaceOrder}
            disabled={placing || !paymentMethod}
          >
            {placing ? 'Placing…' : 'Place Order →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
